import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { ArrowUpRight, Check } from 'lucide-react';
import BlurText from './BlurText';
import { useCountryLink } from '../context/CountryContext';
import { getStartedSteps } from '../mock';

const SERIF = { fontFamily: "'Nacelle', sans-serif", fontWeight: 600 };
const SANS = { fontFamily: "'Nacelle', sans-serif", fontWeight: 400 };

const fadeUp = (delay = 0) => ({
  initial: { filter: 'blur(10px)', opacity: 0, y: 20 },
  whileInView: { filter: 'blur(0px)', opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.2 },
  transition: { duration: 0.8, ease: 'easeOut', delay },
});

const FinalCTA = () => {
  const { link } = useCountryLink();
  const head1 = useRef(null);
  const head1InView = useInView(head1, { once: true, amount: 0.3 });

  return (
    <section
      id="trial"
      className="relative bg-black py-20 sm:py-24 lg:py-36 overflow-hidden"
    >
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse at 50% 50%, rgba(255,110,0,0.10) 0%, rgba(255,110,0,0) 55%)',
        }}
      />

      <div className="relative max-w-5xl mx-auto px-5 lg:px-8">
        {/* Trial heading */}
        <div className="text-center max-w-3xl mx-auto" ref={head1}>
          <motion.div
            {...fadeUp(0.1)}
            className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-[#FF8533]/30 bg-[#FF8533]/[0.06] text-[#FFA66B] text-[11px] tracking-[0.2em] uppercase"
            style={SANS}
          >
            Limited Access · Closing Soon
          </motion.div>

          <div className="mt-7" style={SERIF}>
            {head1InView && (
              <BlurText
                text="Get MBE Alert. Live Zones On Your Telegram."
                highlightWords={['MBE', 'Alert.']}
                highlightClassName="text-[#FF6E00]"
                className="text-4xl sm:text-5xl md:text-6xl lg:text-[4.75rem] text-white leading-[0.9] tracking-[-2px]"
              />
            )}
          </div>

          <motion.p
            {...fadeUp(0.7)}
            className="mt-7 text-white/65 text-base md:text-[17px] leading-relaxed max-w-2xl mx-auto font-light"
            style={SANS}
          >
            Plug it into your Telegram. Feel the difference between watching
            charts and being told the exact moment a valid zone forms.
          </motion.p>
        </div>

        {/* 3 steps */}
        <div className="mt-20 text-center">
          <motion.div
            {...fadeUp(0)}
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/10 bg-white/[0.03] text-white/70 text-[11px] tracking-[0.2em] uppercase"
            style={SANS}
          >
            Connect in under 2 minutes
          </motion.div>
          <motion.h3
            {...fadeUp(0.1)}
            className="mt-5 text-3xl sm:text-4xl md:text-5xl text-white tracking-[-1.5px] leading-[1]"
            style={SERIF}
          >
            Get started in <span className="text-[#FF8533]">3 simple steps</span>
          </motion.h3>
        </div>

        <div className="mt-12 grid md:grid-cols-3 gap-5">
          {getStartedSteps.map((s, i) => (
            <motion.div
              key={i}
              {...fadeUp(i * 0.1)}
              className="relative bg-white/[0.025] border border-white/10 rounded-2xl p-7 hover:border-[#FF8533]/30 hover:bg-white/[0.045] transition-all duration-300"
            >
              <div
                className="text-[#FF8533] text-[60px] leading-none tracking-[-2px]"
                style={SERIF}
              >
                {s.num}
              </div>
              <h4
                className="mt-3 text-white text-2xl tracking-[-0.5px] leading-tight"
                style={SERIF}
              >
                {s.title}
              </h4>
              <p
                className="mt-3 text-white/55 text-[14px] leading-relaxed font-light"
                style={SANS}
              >
                {s.desc}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Final push */}
        <motion.div
          {...fadeUp(0.1)}
          className="mt-24 rounded-3xl bg-gradient-to-br from-[#FF6E00]/[0.12] to-[#FF8533]/[0.03] border border-[#FF8533]/25 p-10 sm:p-16 text-center relative overflow-hidden"
        >
          <div className="absolute -top-20 -right-20 w-[400px] h-[400px] rounded-full bg-[#FF6E00]/15 blur-[100px] pointer-events-none" />
          <div className="relative">
            <h3
              className="text-white text-[32px] sm:text-[44px] lg:text-[56px] tracking-[-2px] leading-[0.95]"
              style={SERIF}
            >
              The trader who reacts on time
              <br />
              <span className="text-[#FF8533]">
                beats the trader who watches longer.
              </span>
            </h3>
            <p
              className="mt-6 text-white/70 text-[15.5px] max-w-xl mx-auto font-light"
              style={SANS}
            >
              MBE Alert isn&apos;t here to replace your judgement. It&apos;s here to make
              sure you never miss the moment to use it.
            </p>
            <a
              href={link} target="_blank" rel="noopener noreferrer"
              className="mt-8 inline-flex items-center gap-2 bg-[#FF6E00] hover:bg-[#FF8533] text-white text-[15px] px-7 py-4 rounded-full transition-colors shadow-[0_22px_50px_-15px_rgba(255,110,0,0.75)]"
              style={{ ...SANS, fontWeight: 500 }}
            >
              Get MBE Alert Now <ArrowUpRight className="w-4 h-4" />
            </a>
            <div
              className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-white/55 text-[12.5px] font-light"
              style={SANS}
            >
              <span className="inline-flex items-center gap-1.5">
                <Check className="w-3.5 h-3.5 text-[#FF8533]" /> Instant Telegram access
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Check className="w-3.5 h-3.5 text-[#FF8533]" /> Live trade webinar
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Check className="w-3.5 h-3.5 text-[#FF8533]" /> Cancel anytime
              </span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default FinalCTA;
